// Local replacement for the abandoned `@react-pdf/styled-components` package
// (which pinned React 16/17 and blocked the React 18 upgrade).
//
// It reproduces the small slice of that package's API this codebase uses:
//   const Foo = styled.View`  css...  `
//   <Foo style={runtimeOverrides}>...</Foo>
// building on @react-pdf/renderer v4 primitives (View, Text, Image, Page, Link).
//
// The tagged-template CSS is parsed once into a react-pdf style object. Template
// interpolations (e.g. ${padding}) are supported. react-pdf v4 is stricter than
// styled-components v1 was, so we normalize/units-fix values and drop CSS
// properties that react-pdf does not understand (which v1 silently ignored).

import React from 'react'
import { View, Text, Image, Page, Link, Note, Canvas } from '@react-pdf/renderer'

const PRIMITIVES = { View, Text, Image, Page, Link, Note, Canvas }

// CSS properties that @react-pdf/renderer does not support. styled-components v1
// tolerated them (they were no-ops in PDF); dropping them keeps v4 from warning.
const UNSUPPORTED_PROPS = new Set([
  'display', // react-pdf is always flex; `display: block/flex` is meaningless
  'white-space',
  'overflow',
  'text-overflow',
  'justify-items',
  'align-content', // not part of react-pdf's yoga subset
  'box-sizing',
  'cursor',
  'float',
  'clear',
  'content',
  'transition',
  'animation',
  'border-radius', // supported, but keep? -> react-pdf DOES support borderRadius; not dropped (see below)
])
// borderRadius is actually supported; remove it from the drop set.
UNSUPPORTED_PROPS.delete('border-radius')

const kebabToCamel = (str) => str.replace(/-([a-z])/g, (_, c) => c.toUpperCase())

// A handful of numeric properties that react-pdf expects as unit strings/numbers.
// styled-components CSS like `bottom: 8;` is fine (react-pdf treats bare numbers
// as points), so we generally pass values through untouched.
function normalizeValue(prop, value) {
  const v = value.trim()
  return v
}

// Parse a CSS declaration block ("a: b; c: d;") into a react-pdf style object.
function parseCss(cssText) {
  const style = {}
  const declarations = cssText.split(';')

  for (const decl of declarations) {
    const idx = decl.indexOf(':')
    if (idx === -1) continue

    const rawProp = decl.slice(0, idx).trim()
    const rawValue = decl.slice(idx + 1).trim()
    if (!rawProp || !rawValue) continue
    if (UNSUPPORTED_PROPS.has(rawProp)) continue

    // `display: flex|block` dropped above via UNSUPPORTED_PROPS.
    const prop = kebabToCamel(rawProp)
    style[prop] = normalizeValue(rawProp, rawValue)
  }

  return style
}

// Build the CSS string from a tagged template's parts + interpolations.
function buildCss(strings, values) {
  let out = ''
  strings.forEach((s, i) => {
    out += s
    if (i < values.length) {
      const v = values[i]
      out += typeof v === 'function' ? '' : v == null ? '' : String(v)
    }
  })
  return out
}

function createStyledComponent(Component) {
  return (strings, ...values) => {
    const baseStyle = parseCss(buildCss(strings, values))

    const StyledPdfComponent = ({ style, children, ...rest }) => {
      const mergedStyle = Array.isArray(style)
        ? [baseStyle, ...style]
        : style
        ? [baseStyle, style]
        : baseStyle
      return (
        <Component style={mergedStyle} {...rest}>
          {children}
        </Component>
      )
    }

    StyledPdfComponent.displayName = `Styled(${Component.displayName || Component.name || 'PdfComponent'})`
    return StyledPdfComponent
  }
}

// styled.View`...`, styled.Text`...`, etc.
const styled = new Proxy(
  {},
  {
    get(_target, key) {
      const Component = PRIMITIVES[key]
      if (!Component) {
        throw new Error(`styled-pdf: unsupported react-pdf primitive "styled.${String(key)}"`)
      }
      return createStyledComponent(Component)
    },
  }
)

export default styled
